import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { formatCaseUnits } from '@/lib/formatCaseUnits';
import { ProductIdentity } from '@/components/products';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';

interface ReceiptLine {
  id: string;
  productId: string;
  quantity: number;
  casesReceived: number;
  leftoverUnits: number;
  batchNumber: string | null;
  expiryDate: string | null;
  product?: {
    id: string;
    sku: string;
    name: string;
    unitsPerCase?: number;
    imageUrl?: string | null;
  };
}

interface GoodsReceiptDetail {
  id: string;
  receiptNumber: string;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  receiptDate: string;
  invoiceReference: string | null;
  invoiceImageUrl: string | null;
  notes: string | null;
  warehouse?: { name: string; code: string };
  supplier?: { name: string; code: string };
  lines?: ReceiptLine[];
}

export default function GoodsReceiptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<GoodsReceiptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceRef, setInvoiceRef] = useState('');
  const [receiving, setReceiving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/goods-receipts/${id}`);
      if (!res.ok) throw new Error('Goods receipt not found');
      const data: GoodsReceiptDetail = await res.json();
      setReceipt(data);
      setInvoiceRef(data.invoiceReference || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveInvoiceRef() {
    if (!id) return;
    const res = await apiFetch(`/api/v1/goods-receipts/${id}/invoice-reference`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceReference: invoiceRef.trim() || null }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(formatApiErrorBody(data));
      return;
    }
    await load();
  }

  async function uploadInvoice(file: File) {
    if (!id) return;
    setUploading(true);
    setError(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await apiFetch(`/api/v1/goods-receipts/${id}/invoice-image`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorBody(data));
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleReceive() {
    if (!id) return;
    setReceiving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/goods-receipts/${id}/receive`, { method: 'PATCH' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorBody(data));
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Receive failed');
    } finally {
      setReceiving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="space-y-6">
        <p className="text-destructive">{error || 'Not found'}</p>
        <Button variant="link" onClick={() => navigate('/goods-receipts')}>Back</Button>
      </div>
    );
  }

  const isPending = receipt.status === 'PENDING';
  const imageSrc = receipt.invoiceImageUrl?.startsWith('http')
    ? receipt.invoiceImageUrl
    : receipt.invoiceImageUrl
      ? `${receipt.invoiceImageUrl}`
      : null;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/goods-receipts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h1 className="text-2xl font-semibold">Goods Receipt {receipt.receiptNumber}</h1>
          <Badge variant={receipt.status === 'COMPLETED' ? 'default' : 'secondary'}>
            {receipt.status}
          </Badge>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
        )}

        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>Warehouse: {receipt.warehouse?.name}</p>
            <p>Supplier: {receipt.supplier?.name}</p>
            <p>Date: {new Date(receipt.receiptDate).toLocaleDateString()}</p>
            {receipt.notes && <p>Notes: {receipt.notes}</p>}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Invoice</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label>Invoice reference</Label>
                <Input
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                  disabled={!isPending}
                />
              </div>
              {isPending && (
                <Button type="button" variant="outline" onClick={saveInvoiceRef}>
                  Save
                </Button>
              )}
            </div>
            {imageSrc ? (
              <div>
                <a href={imageSrc} target="_blank" rel="noreferrer" className="text-primary underline text-sm">
                  View invoice image
                </a>
              </div>
            ) : isPending ? (
              <div>
                <Label htmlFor="invoiceFile">Upload invoice image</Label>
                <Input
                  id="invoiceFile"
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadInvoice(f);
                  }}
                />
                {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No invoice image</p>
            )}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Lines</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(receipt.lines ?? []).map((line) => {
                const upc = line.product?.unitsPerCase ?? 1;
                return (
                  <div key={line.id} className="flex justify-between border-b py-2 text-sm">
                    <ProductIdentity
                      productId={line.productId}
                      name={line.product?.name ?? line.productId}
                      sku={line.product?.sku}
                      imageUrl={line.product?.imageUrl}
                      size="sm"
                      showSku={false}
                    />
                    <span className="text-muted-foreground">
                      {formatCaseUnits(line.quantity, upc)} ({line.quantity} units)
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {isPending && (
          <Button onClick={handleReceive} disabled={receiving}>
            {receiving ? 'Receiving...' : 'Receive stock'}
          </Button>
        )}
      </div>
    </div>
  );
}
